import React, { useState } from 'react';
import { Button, Space, message, Card, Typography, Form, Input, Alert } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { authService } from '../../services/auth.service';
import { marketingService } from '../../services/marketing.service';
import { getUserFriendlyMessage } from '../../utils/error-handler';
import { FormErrors } from '../common/FormErrors';

const { Title, Text } = Typography;

interface ConfirmAccountFormProps {
  email: string;
  userId?: string;
  onSuccess?: () => void;
  onBackToLogin?: () => void;
}

export const ConfirmAccountForm: React.FC<ConfirmAccountFormProps> = ({
  email,
  userId,
  onSuccess,
  onBackToLogin,
}) => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [form] = Form.useForm();

  const handleConfirm = async (values: { code: string }) => {
    try {
      setLoading(true);
      setApiError(null);

      await authService.confirmSignUp(email, values.code);
      marketingService.pushEvent('email_confirmado', { user_id: userId ?? '' });

      message.success('Conta confirmada com sucesso! Você já pode fazer login.');

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Confirmation error:', err);
      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setResendLoading(true);
      setApiError(null);
      setResendSuccess(false);

      await authService.resendConfirmationCode(email);

      setResendSuccess(true);
      message.success('Código reenviado com sucesso! Verifique seu email.');
    } catch (err: any) {
      console.error('Resend code error:', err);
      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 400, margin: '0 auto', padding: '40px 20px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <MailOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            <Title level={2}>Confirme sua conta</Title>
            <Text type="secondary">
              Enviamos um código de verificação para:
              <br />
              <strong>{email}</strong>
            </Text>
          </div>

          {apiError && (
            <FormErrors message={apiError} showIcon />
          )}

          {resendSuccess && (
            <Alert
              message="Sucesso!"
              description="Código reenviado com sucesso! Verifique seu email para o novo código."
              type="success"
              showIcon
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleConfirm}
            autoComplete="off"
          >
            <Form.Item
              name="code"
              label="Código de Verificação"
              rules={[
                {
                  required: true,
                  message: 'Código é obrigatório',
                },
                {
                  pattern: /^\d{6}$/,
                  message: 'O código deve ter exatamente 6 dígitos',
                },
              ]}
              help="Digite o código de 6 dígitos enviado para seu email"
            >
              <Input
                placeholder="000000"
                maxLength={6}
                prefix={<LockOutlined />}
                autoComplete="off"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
                block
              >
                Confirmar Conta
              </Button>
            </Form.Item>
          </Form>

          <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
            <Text type="secondary">Não recebeu o código?</Text>
            <Button
              type="link"
              onClick={handleResendCode}
              loading={resendLoading}
              disabled={resendLoading || loading}
            >
              Reenviar código
            </Button>

            {onBackToLogin && (
              <Button type="link" onClick={onBackToLogin} disabled={loading}>
                Voltar para login
              </Button>
            )}
          </Space>
        </Space>
      </Card>
    </div>
  );
};
